import React, { useState, useEffect, useRef } from "react";
import styles from "./RecordPaymentModal.module.css";
import WebService from "../Services/WebService";
import { toast } from "react-toastify";
import Select from "react-select";
import CloseButton from "../Customs/Button/CrossButton";

interface Customer {
  user_id: number;
  customer_id: number;
  username: string;
  [key: string]: any;
}

interface RecordPaymentModalProps {
  open: boolean;
  onClose: () => void;
  customerDropdownDisabled?: boolean;
  selectedCustomerId?: number;
  outstanding?: number | string;
  credit?: number | string;
  onSuccess?: () => void;
}

const PAYMENT_METHODS = [
  { value: "CASH", label: "Cash" },
  { value: "UPI", label: "Online UPI" },
  { value: "BANK_TRANSFER", label: "Bank Transfer" },
];

function formatMoney(value: number | string | null | undefined): string {
  if (value === null || value === undefined || value === "" || value === "null") {
    return "--";
  }
  const num = typeof value === "number" ? value : Number(value);
  if (Number.isNaN(num)) return "--";
  return `₹${num.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

const RecordPaymentModal: React.FC<RecordPaymentModalProps> = ({
  open,
  onClose,
  customerDropdownDisabled,
  selectedCustomerId,
  outstanding: outstandingProp,
  credit: creditProp,
  onSuccess,
}) => {
  const [customerList, setCustomerList] = useState<Customer[]>([]);
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [paymentDate, setPaymentDate] = useState("");
  const [amount, setAmount] = useState("");
  const [paymentMethod, setPaymentMethod] = useState(PAYMENT_METHODS[0].value);
  const [reference, setReference] = useState("");
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(false);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [fetchedOutstanding, setFetchedOutstanding] = useState<number | null>(null);
  const [fetchedCredit, setFetchedCredit] = useState<number | null>(null);
  const searchTimeout = useRef<NodeJS.Timeout | null>(null);
  const summaryRequestId = useRef(0);

  const outstandingValue =
    fetchedOutstanding !== null
      ? fetchedOutstanding
      : outstandingProp !== undefined
      ? outstandingProp
      : null;
  const creditValue =
    fetchedCredit !== null
      ? fetchedCredit
      : creditProp !== undefined
      ? creditProp
      : null;

  const summaryOutstanding = !customer
    ? "-:-"
    : summaryLoading
    ? "Loading..."
    : formatMoney(outstandingValue);
  const summaryCredit = !customer
    ? "-:-"
    : summaryLoading
    ? "Loading..."
    : formatMoney(creditValue);

  const fetchAccountSummary = (userId: number) => {
    const requestId = ++summaryRequestId.current;
    setSummaryLoading(true);
    WebService.getAPI<any>({
      // No date filter — total outstanding / credit for the account
      action: `/v1/door-management/accounts/${userId}?limit=1&page_number=1`,
    })
      .then((res) => {
        if (requestId !== summaryRequestId.current) return;
        const account = res?.data?.accounts;
        const outstanding =
          account?.overall_due_amount ??
          account?.outstanding ??
          account?.due_amount ??
          0;
        const credit =
          account?.credit_limit ?? account?.credit ?? 0;
        setFetchedOutstanding(
          outstanding === null || outstanding === undefined || outstanding === ""
            ? 0
            : Number(outstanding)
        );
        setFetchedCredit(
          credit === null || credit === undefined || credit === ""
            ? 0
            : Number(credit)
        );
      })
      .catch(() => {
        if (requestId !== summaryRequestId.current) return;
        setFetchedOutstanding(null);
        setFetchedCredit(null);
      })
      .finally(() => {
        if (requestId === summaryRequestId.current) setSummaryLoading(false);
      });
  };

  // Prefill customer when opened from customer detail
  useEffect(() => {
    if (!open || !selectedCustomerId) return;

    const idNum = Number(selectedCustomerId);
    let found = customerList.find((c) => Number(c.user_id) === idNum);

    if (!found) {
      WebService.getAPI<any>({
        action: `/v1/door-management/customers?user_id=${idNum}`,
      })
        .then((res) => {
          let arr = Array.isArray(res?.data?.users) ? res.data.users : [];
          if (arr.length) {
            setCustomerList((prev) => {
              const already = prev.some((c) => c.user_id === arr[0].user_id);
              return already ? prev : [...prev, arr[0]];
            });
            setCustomer(arr[0]);
          } else {
            setCustomer(null);
          }
        })
        .catch(() => setCustomer(null));
    } else {
      setCustomer(found);
    }
  }, [open, selectedCustomerId, customerList]);

  // Live customer search
  useEffect(() => {
    if (!open || !searchTerm.trim() || selectedCustomerId) return;

    if (searchTimeout.current) clearTimeout(searchTimeout.current);

    searchTimeout.current = setTimeout(() => {
      setLoading(true);
      WebService.getAPI<any>({
        action: `/v1/door-management/customers?user_like=${encodeURIComponent(searchTerm)}`,
      })
        .then((res) => {
          const arr = Array.isArray(res?.data?.users) ? res.data.users : [];
          setCustomerList(arr);
        })
        .catch(() => setCustomerList([]))
        .finally(() => setLoading(false));
    }, 300);
  }, [open, searchTerm, selectedCustomerId]);

  // Load outstanding / credit whenever the selected customer changes
  useEffect(() => {
    if (!open || !customer?.user_id) {
      setFetchedOutstanding(null);
      setFetchedCredit(null);
      return;
    }
    fetchAccountSummary(Number(customer.user_id));
  }, [open, customer?.user_id]);

  useEffect(() => {
    if (!open) {
      setCustomerList([]);
      setCustomer(null);
      setSearchTerm("");
      setPaymentDate("");
      setAmount("");
      setPaymentMethod(PAYMENT_METHODS[0].value);
      setReference("");
      setNotes("");
      setError(null);
      setFetchedOutstanding(null);
      setFetchedCredit(null);
      setSummaryLoading(false);
    }
  }, [open]);

  if (!open) return null;

  const validate = () => {
    if (!customer) return "Customer is required";
    if (!paymentDate) return "Payment date is required";
    if (!amount || Number(amount) <= 0) return "Amount must be greater than zero";
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const errMsg = validate();
    if (errMsg) {
      setError(errMsg);
      toast.error(errMsg);
      return;
    }
    if (!customer) {
      setError("Customer is required");
      toast.error("Customer is required");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        user_id: customer.user_id,
        customer_id: customer.customer_id,
        payment_date: paymentDate ? `${paymentDate}T00:00:00Z` : "",
        amount_received: Number(amount),
        payment_method: paymentMethod,
        reference_number: reference,
        notes,
      };
      const res = await WebService.postAPI<any>({
        action: "/v1/door-management/accounts/recordpayment",
        body: payload,
      });
      const receiptNo = res?.data?.receipt_number;
      const allocations = Array.isArray(res?.data?.allocations) ? res.data.allocations : [];
      toast.success(
        allocations.length > 1
          ? `Payment recorded across ${allocations.length} orders${receiptNo ? ` (Receipt ${receiptNo})` : ""}`
          : receiptNo
          ? `Payment recorded. Receipt ${receiptNo} issued.`
          : res?.data?.message || "Payment recorded successfully!"
      );

      // Refresh account financials so parent/detail views see updated outstanding
      fetchAccountSummary(Number(customer.user_id));

      setLoading(false);
      if (typeof onSuccess === "function") onSuccess();
      if (typeof onClose === "function") onClose();
    } catch (err: any) {
      setLoading(false);
    }
  };

  return (
    <div className={styles.overlay}>
      <div className={styles.modal}>
        <div className={styles.header}>
          <h2>Record Payment</h2>
          <CloseButton onClick={onClose} className={styles.closeBtn} />
        </div>

        {error && <div className={styles.apiError}>{error}</div>}

        <form className={styles.form} onSubmit={handleSubmit}>
          <div className={styles.formRow}>
            <label>Customer</label>
            <Select
              classNamePrefix="rs"
              placeholder="Search & select customer..."
              isClearable
              isDisabled={customerDropdownDisabled}
              value={
                customer
                  ? {
                      value: customer.user_id,
                      label: customer.company_name
                        ? `${customer.company_name} (${customer.username})`
                        : customer.username,
                    }
                  : null
              }
              options={customerList.map((c) => ({
                value: c.user_id,
                label: c.company_name
                  ? `${c.company_name} (${c.username})`
                  : c.username,
              }))}
              onInputChange={(inputValue, { action }) => {
                if (action === "input-change") setSearchTerm(inputValue);
              }}
              onChange={(option) => {
                const found = customerList.find((c) => c.user_id === option?.value);
                setCustomer(found || null);
              }}
              filterOption={null}
              isLoading={loading}
              noOptionsMessage={() =>
                searchTerm
                  ? loading
                    ? "Searching..."
                    : "No customers found"
                  : "Type to search customers"
              }
            />
          </div>

          <div className={styles.summaryBox}>
            <div>
              <b>Total Outstanding:</b> <span>{summaryOutstanding}</span>
            </div>
            <div>
              <b>Current Credit:</b> <span>{summaryCredit}</span>
            </div>
          </div>

          <div className={styles.formRow2Col}>
            <div>
              <label>Payment Date</label>
              <input
                type="date"
                className={styles.input}
                value={paymentDate}
                onChange={(e) => setPaymentDate(e.target.value)}
                max={new Date().toISOString().split("T")[0]}
              />
            </div>
            <div>
              <label>Amount Received</label>
              <input
                type="number"
                className={styles.input}
                min="0"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="0.00"
              />
            </div>
          </div>

          <div className={styles.formRow2Col}>
            <div>
              <label>Payment Method</label>
              <select
                className={styles.input}
                value={paymentMethod}
                onChange={(e) => setPaymentMethod(e.target.value)}
              >
                {PAYMENT_METHODS.map((m) => (
                  <option value={m.value} key={m.value}>
                    {m.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label>Reference Number (Optional)</label>
              <input
                className={styles.input}
                value={reference}
                onChange={(e) => setReference(e.target.value)}
                placeholder="Transaction ID, Check No., etc."
              />
            </div>
          </div>

          <div className={styles.formRow}>
            <label>Notes (Optional)</label>
            <textarea
              className={styles.input}
              rows={3}
              placeholder="Add any additional notes about this payment..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </div>

          <div className={styles.footer}>
            <button
              type="button"
              className={styles.cancelBtn}
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className={styles.submitBtn}
              disabled={loading}
            >
              {loading ? "Recording..." : "Record Payment"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RecordPaymentModal;
